// src/components/FamilyTreeMarquee.tsx
import alfoaimLogo from "@/assets/images/alfoaim.webp";
import alhamdanLogo from "@/assets/images/alhamdan.webp";
import alhassonLogo from "@/assets/images/alhasson.webp";
import aljotaliLogo from "@/assets/images/aljotali.webp";
import alkhudiryLogo from "@/assets/images/alkhudiry.webp";
import alrishodyLogo from "@/assets/images/alrishody.webp";
import alsaawiLogo from "@/assets/images/alsaawi.webp";
import alsawweLogo from "@/assets/images/alsawwe.jpeg";
import alsofainLogo from "@/assets/images/alsofain.jpg";
import alzomieLogo from "@/assets/images/alzomie.jpeg";
import { Heading } from "@/components/typography/Heading";
import clsx from "clsx";
import React, { useEffect, useMemo, useRef, useState } from "react";

interface Branch {
  id: string | number;
  name: string;
  roundedCount: number;
}

interface Props {
  branchesNames: Branch[];
}

const MIN_ITEMS_PER_SLIDER = 10;
const ROWS = [
  { speed: 34, direction: "left", gradient: "from-green-500 to-green-600" },
  { speed: 30, direction: "right", gradient: "from-green-600 to-green-700" },
  { speed: 34, direction: "left", gradient: "from-green-700 to-green-800" },
] as const;

export const Clients: React.FC<Props> = ({ branchesNames }) => {
  const logos = [
    { src: alrishodyLogo, alt: "Al Rishody Family Logo" },
    { src: alsaawiLogo, alt: "Al Saawi Family Logo" },
    { src: aljotaliLogo, alt: "Al Jotali Family Logo" },
    { src: alkhudiryLogo, alt: "Al Khudiry Family Logo" },
    { src: alsawweLogo, alt: "Al Sawwe Family Logo" },
    { src: alzomieLogo, alt: "Al Zomie Family Logo" },
    { src: alfoaimLogo, alt: "Al Foaim Family Logo" },
    { src: alhassonLogo, alt: "Al Hasson Family Logo" },
    { src: alsofainLogo, alt: "Al Sofain Family Logo" },
    { src: alhamdanLogo, alt: "Al Hamdan Family Logo" },
  ];

  const branchRows = useMemo(() => splitBranchRows(branchesNames), [branchesNames]);

  return (
    <section className="w-full overflow-hidden py-12 md:py-16">
      <div className="relative mx-auto">
        <div className="mb-12 text-center md:mb-16">
          <Heading level={2} size="section" className="px-2">
            أكبر منصة شجرات عائلة بالعالم
          </Heading>
        </div>

        <div className="mb-12 px-4 md:mb-16">
          <div className="grid grid-cols-4 place-items-center gap-6 sm:grid-cols-5 md:grid-cols-8 lg:grid-cols-10">
            {logos.map((logo) => (
              <img
                key={logo.alt}
                className="aspect-square w-full max-w-[80px] rounded-xl object-contain opacity-70 transition duration-300 ease-in-out hover:opacity-100"
                src={logo.src}
                alt={logo.alt}
                loading="lazy"
              />
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {branchRows.map((items, index) => {
            const row = ROWS[index];

            // Each branch lands in exactly one row, so the row's first branch identifies it.
            return <ConstantSpeedMarquee key={items[0].id} items={items} row={row} />;
          })}
        </div>
      </div>
    </section>
  );
};

interface ConstantSpeedMarqueeProps {
  items: Branch[];
  row: (typeof ROWS)[number];
}

function ConstantSpeedMarquee({ items, row }: ConstantSpeedMarqueeProps) {
  const segmentRef = useRef<HTMLDivElement>(null);
  const [segmentWidth, setSegmentWidth] = useState(0);
  const marqueeItems = useMemo(() => fillMarqueeItems(items), [items]);

  useEffect(() => {
    const segment = segmentRef.current;

    if (!segment) {
      return;
    }

    const updateWidth = () => setSegmentWidth(segment.scrollWidth);

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(segment);

    return () => observer.disconnect();
  }, [marqueeItems]);

  if (!items.length) {
    return null;
  }

  const duration = segmentWidth ? segmentWidth / row.speed : 1;

  return (
    <div className="marquee-container" dir="ltr">
      <div
        className="marquee-track"
        data-direction={row.direction}
        style={
          {
            "--marquee-distance": `-${segmentWidth}px`,
            "--marquee-duration": `${duration}s`,
          } as React.CSSProperties
        }
      >
        {[false, true].map((hidden) => (
          <div
            key={hidden ? "copy" : "original"}
            ref={hidden ? undefined : segmentRef}
            className="marquee-segment"
            aria-hidden={hidden || undefined}
          >
            {marqueeItems.map(({ key, branch }) => (
              <FamilyBranchCard key={key} branch={branch} gradient={row.gradient} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function FamilyBranchCard({ branch, gradient }: { branch: Branch; gradient: string }) {
  return (
    <div
      className={clsx(
        "marquee-item mx-2 flex h-[80px] min-w-[140px] flex-shrink-0 cursor-pointer flex-col items-center justify-center rounded-xl",
        `bg-gradient-to-br ${gradient} text-white`,
        "transition-all duration-300 ease-out hover:-translate-y-1.5 hover:brightness-110",
      )}
      dir="rtl"
    >
      <div
        className="mb-1 w-full max-w-[110px] overflow-hidden px-1 text-center text-base font-semibold text-ellipsis whitespace-nowrap"
        title={branch.name}
      >
        {branch.name}
      </div>
      <div className="inline-flex items-baseline gap-1 rounded-full bg-white/20 px-2.5 py-1 text-xs font-medium opacity-95">
        <span>{branch.roundedCount}</span>
        <span className="text-[0.7rem] opacity-80">فرد</span>
      </div>
    </div>
  );
}

function splitBranchRows(items: Branch[]): Branch[][] {
  const rowCount = Math.min(ROWS.length, Math.ceil(items.length / MIN_ITEMS_PER_SLIDER));

  return Array.from({ length: rowCount }, (_, rowIndex) =>
    items.filter((_, itemIndex) => itemIndex % rowCount === rowIndex),
  ).filter((row) => row.length);
}

/** Short rows repeat their branches to fill the track; the copy number keeps each repeated card's key unique. */
function fillMarqueeItems(items: Branch[]): { key: string; branch: Branch }[] {
  const length = items.length ? Math.max(items.length, MIN_ITEMS_PER_SLIDER) : 0;

  return Array.from({ length }, (_, position) => {
    const branch = items[position % items.length];

    return { key: `${branch.id}-copy-${Math.floor(position / items.length)}`, branch };
  });
}
